import { FhirBundleSdk } from "@metriport/fhir-sdk";
import { TableRow } from "../utils/tables-metadata";
import { IndividualLabTable, LabPanelRowData, PanelLabTable } from "./lab";
import { SearchNotesTable, SearchImagingReportsTable } from "./notes";
import { RawDocumentsTable } from "./raw-documents";
import { renderSharedTable } from "./shared-table-content";

interface SearchTableContentProps {
  tableType: string;
  displayName: string;
  filteredRows: TableRow[];
  fhirSdk: FhirBundleSdk;
  filterQuery: string;
  handleRowClick: (row: TableRow, tableType: string, suspectsContext?: string) => void;
  handlePdfView: (docId: string) => void;
  isRowViewed: (tableType: string, rowId: string) => boolean;
  onDocumentDownloaded?: (docId: string) => void;
}

/**
 * Table dispatcher for search mode.
 * Uses shared rendering for common tables, with search-specific handling for:
 * - DiagnosticReport: SearchNotesTable with snippets
 * - ImagingReports: SearchImagingReportsTable with snippets
 * - LaboratoryObservations: IndividualLabTable (no toggle)
 * - LaboratoryObservationsPanel: PanelLabTable as separate section
 * - DocumentReference: RawDocumentsTable with filter query
 */
export function SearchTableContent({
  tableType,
  displayName,
  filteredRows,
  fhirSdk,
  filterQuery,
  handleRowClick,
  handlePdfView,
  isRowViewed,
  onDocumentDownloaded,
}: SearchTableContentProps): JSX.Element | null {
  const isRowViewedHelper = (row: TableRow) => isRowViewed(tableType, row.id);

  const commonProps = {
    fhirSdk,
    filterQuery,
    onPdfView: handlePdfView,
    onRowClick: (row: TableRow) => handleRowClick(row, tableType),
    isRowViewed: isRowViewedHelper,
  };

  // Try shared table rendering first
  const sharedResult = renderSharedTable({
    tableType,
    displayName,
    rows: filteredRows,
    commonProps,
  });
  if (sharedResult !== null) {
    return sharedResult;
  }

  // Search-specific table handling
  switch (tableType) {
    case "DiagnosticReport":
      return <SearchNotesTable rows={filteredRows} {...commonProps} displayName={displayName} />;

    case "ImagingReports":
      return (
        <SearchImagingReportsTable rows={filteredRows} {...commonProps} displayName={displayName} />
      );

    case "LaboratoryObservations":
      // Search mode: IndividualLabTable WITHOUT toggle (separate section for panels)
      return (
        <IndividualLabTable
          rows={filteredRows as never[]}
          onRowClick={commonProps.onRowClick}
          filterQuery={filterQuery}
          displayName={displayName}
          isRowViewed={commonProps.isRowViewed}
        />
      );

    case "LaboratoryObservationsPanel":
      // Search mode: PanelLabTable WITHOUT toggle (as separate searchable section)
      return (
        <PanelLabTable
          rows={filteredRows as LabPanelRowData[]}
          fhirSdk={fhirSdk}
          onRowClick={commonProps.onRowClick}
          filterQuery={filterQuery}
          displayName={displayName}
          isRowViewed={commonProps.isRowViewed}
        />
      );

    case "DocumentReference":
      return (
        <RawDocumentsTable
          rows={filteredRows}
          fhirSdk={fhirSdk}
          filterQuery={filterQuery}
          // eslint-disable-next-line @typescript-eslint/no-empty-function
          onRowClick={() => {}}
          onDocumentDownloaded={onDocumentDownloaded}
          isRowViewed={isRowViewedHelper}
        />
      );

    default:
      console.warn(`Unknown tableType in search mode: ${tableType}`);
      return null;
  }
}
